// Story 5.2 — Fee-breakdown math shared by the ListForSaleDialog UI.
//
// Mirrors the backend constants at
// server/src/services/marketplace_listing_service.rs. The backend is
// the wire-canonical source of truth (the response carries the
// server-computed values); this client-side mirror is for the live
// preview as the user types.

export const PLATFORM_FEE_BPS = 1_000;
export const CREATOR_ROYALTY_BPS = 500;
export const SELLER_PROCEEDS_BPS = 8_500;
export const TOTAL_BPS = 10_000;
// €0.20 floor: the creator's 5% royalty truncates to 0 cents below €0.20, so
// this is the lowest price at which the featured creator still earns ≥ €0.01
// on a resale. Mirrors the backend SHARE_LISTING_PRICE_MIN_CENTS.
export const SHARE_LISTING_PRICE_MIN_CENTS = 20;
/// Story 5.3 — sealed-dossier listing floor (€1.00 per FR42). 10x the
/// share floor; mirrors the backend SEALED_DOSSIER_LISTING_PRICE_MIN_CENTS.
export const SEALED_DOSSIER_LISTING_PRICE_MIN_CENTS = 100;

export interface FeeBreakdown {
  platformFeeCents: number;
  creatorRoyaltyCents: number;
  estimatedProceedsCents: number;
}

/**
 * Computes the 10/5/85 fee split with platform absorbing the
 * integer-cent rounding remainder (FR45). Mirrors the backend's
 * compute_fee_breakdown so the live preview matches the response.
 */
export function computeFeeBreakdown(priceCents: number): FeeBreakdown {
  if (!Number.isFinite(priceCents) || priceCents < 0) {
    return {
      platformFeeCents: 0,
      creatorRoyaltyCents: 0,
      estimatedProceedsCents: 0,
    };
  }
  const price = Math.trunc(priceCents);
  const creatorRoyaltyCents = Math.trunc((price * CREATOR_ROYALTY_BPS) / TOTAL_BPS);
  const estimatedProceedsCents = Math.trunc((price * SELLER_PROCEEDS_BPS) / TOTAL_BPS);
  const platformFeeCents = price - creatorRoyaltyCents - estimatedProceedsCents;
  return {
    platformFeeCents,
    creatorRoyaltyCents,
    estimatedProceedsCents,
  };
}

/** Convert a euro string ("1.23") to cents (123). Returns NaN on parse failure. */
export function eurosStringToCents(input: string): number {
  const trimmed = input.trim();
  if (trimmed === "") return NaN;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return NaN;
  return Math.round(n * 100);
}

/** Format a cents amount as "€X.XX". */
export function formatCents(cents: number): string {
  if (!Number.isFinite(cents)) return "€—";
  return `€${(cents / 100).toFixed(2)}`;
}
